import { FormEvent, useEffect, useRef, useState } from "react";

//ASSETS
import LeftArrow from "assets/icons/ArrowLeftHovered.svg";
import RedLoop from "assets/svg/SearchIconRed.svg";
import CoverPlaceholder from "assets/img/CoverPlaceholder.png";
import { ReactComponent as NoDataDark } from "assets/svg/NoDataDark.svg";

// STYLES
import {
  BackToGuidesButton,
  BackToGuidesImage,
  BackToGuidesWrapper,
  CardContainer,
  FlexContainer,
  IframeContainer,
  ImgStyled,
  InputContainer,
  NoDataContainer,
  OfficialAnnouncementsContainer,
  SearchSubmitButton,
  SvgContainer,
  TitleWrapper,
  Wrapper,
} from "./OfficialAnnouncementsStyled";

// LIBRARIES
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// MISC
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { appRoutes } from "config/AppRoutesConfig";
import { COLORS } from "constants/variables";
import { uploadBaseUrl } from "services/base";

// COMPONENTS
import CustomText from "components/Atoms/CustomText/CustomText";
import InputAtom from "components/Atoms/InputAtom/InputAtom";
import Pagination from "components/Atoms/Paginations/Pagination";
import NewsCard from "components/NewsCard/NewsCard";

// REDUX
import { useSelector } from "react-redux";
import newsAndMediaService from "services/newsAndMediaService";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// CONFIGURATION

const OfficialAnnouncements = () => {
  // PROPERTIES

  // API REQUESTS

  // The tabs for the menu

  // Function to handle tab selection

  // LIBRARY CONSTANTS
  const { t } = useTranslation();
  const navigate = useNavigate();
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);

  // STATE CONSTANTS
  const topRef = useRef<HTMLDivElement>(null);
  const [inputText, setInputText] = useState<string>("");
  // Keep the draft separate from the submitted query so typing does not fetch.
  const [search, setSearch] = useState({ query: "", page: 1 });
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [articles, setArticles] = useState<any>();
  const pageSize = 6;

  // LIFE CYCLE
  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setHasError(false);
    newsAndMediaService
      .getNewsArticlesByCategory(search.page, pageSize, search.query, 1, {
        searchContent: true,
        ignoreDiacritics: true,
        signal: controller.signal,
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        setArticles(data);
        setTotalPages(data?.meta?.pagination?.pageCount ?? 0);
        setIsLoading(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setHasError(true);
        setIsLoading(false);
      });
    return () => controller.abort();
  }, [search]);

  // EVENT HANDLERS

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setSearch({ query: inputText.trim(), page: 1 });
  };

  const handlePageChange = (page: number) => {
    setIsLoading(true);
    setSearch((previous) => ({ ...previous, page }));
    topRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <OfficialAnnouncementsContainer ref={topRef}>
      <BackToGuidesWrapper
        onClick={() => navigate(appRoutes.news)}
        biggerCursor={biggerCursor}
      >
        <BackToGuidesImage src={LeftArrow} alt="backIcon" />
        <BackToGuidesButton biggerCursor={biggerCursor}>
          <CustomText
            text={t("view.newsAndMedia.officialAnnouncements.backButton")}
            type={TextType.H5boldspaced}
            color={COLORS.RaisinBlack}
          />
        </BackToGuidesButton>
      </BackToGuidesWrapper>
      <TitleWrapper>
        <CustomText
          text={t("view.newsAndMedia.communications")}
          type={TextType.H2}
        />
      </TitleWrapper>
      <InputContainer>
        <form role="search" onSubmit={handleSearch}>
          <InputAtom
            startAdornment={<ImgStyled src={RedLoop} alt="RedLoopIcon" />}
            endAdornment={
              <SearchSubmitButton type="submit" $biggerCursor={biggerCursor}>
                <CustomText
                  text={t("view.newsAndMedia.officialAnnouncements.search")}
                  type={TextType.H5}
                  color="inherit"
                />
              </SearchSubmitButton>
            }
            placeholder={t(
              "view.newsAndMedia.officialAnnouncements.searchPlaceholder"
            )}
            isSearchBar={true}
            value={inputText}
            onChange={(e) => {
              const value = e.target.value;
              setInputText(value);
              if (!value.trim()) {
                setIsLoading(true);
                setSearch({ query: "", page: 1 });
              }
            }}
          />
        </form>
      </InputContainer>
      {isLoading || hasError ? (
        <NoDataContainer role={hasError ? "alert" : "status"} aria-live="polite">
          <CustomText
            text={t(hasError
              ? "view.newsAndMedia.officialAnnouncements.searchError"
              : "common.loading")}
            type={TextType.P1}
            color={COLORS.RaisinBlack}
          />
        </NoDataContainer>
      ) : articles?.data?.length > 0 ? (
        <>
          <Wrapper>
            <CardContainer>
              {articles?.data.map((card) => {
                return (
                  <NewsCard
                    key={card.id}
                    id={card?.id}
                    img={
                      card?.attributes?.cover?.data?.attributes?.url?.trim()
                        ? `${uploadBaseUrl}${card?.attributes?.cover?.data?.attributes?.url}`
                        : CoverPlaceholder
                    }
                    title={card?.attributes?.title}
                    timeToRead={card?.attributes?.reading_time || ""}
                    timeline={card?.attributes?.article_date}
                    route={appRoutes.officialsDetails}
                  />
                );
              })}
            </CardContainer>
          </Wrapper>
          {totalPages > 1 && (
            <Pagination
              totalPages={totalPages}
              currentPage={search.page}
              onPageChange={handlePageChange}
            />
          )}
          <FlexContainer>
            <IframeContainer>
              <iframe
                src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fm.facebook.com%2Fdepartamenturgente%2F%3Flocale%3Dro_RO&tabs=timeline&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true&appId&width=500&height=600"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              ></iframe>
            </IframeContainer>
          </FlexContainer>
        </>
      ) : (
        <NoDataContainer role="status" aria-live="polite">
          <SvgContainer>
            <NoDataDark />
          </SvgContainer>
          <CustomText
            text={search.query
              ? t("view.newsAndMedia.officialAnnouncements.noSearchResults", { query: search.query })
              : t("app.noData")}
            type={TextType.P1}
            color={COLORS.MetalicSilver}
          />
        </NoDataContainer>
      )}
    </OfficialAnnouncementsContainer>
  );
};

export default OfficialAnnouncements;
